import React, { useState } from 'react';
import { Menu, MenuItem } from '../../Menu';
import { CHANNELS } from '../../../domain/notifications';
import {
  allowsMakeupCredits, categoryIsBillable, categoryLeadHours, leadLabel,
} from '../../../domain/categories';
import s from './categories.module.css';

// Categories carry a colour, a billable flag and a reminder lead time per
// channel (#87). The billable flag means a "Recital" or "Unavailable" block
// never generates a charge however the student is priced, set once on the
// category.
//
// The row is a summary: every field is edited in the Edit Category drawer
// reached from the row's ⋮, as in MMS (#98).

const remindersOf = (c) => {
  const email = categoryLeadHours(c, CHANNELS.email);
  const sms = categoryLeadHours(c, CHANNELS.sms);
  if (email === null && sms === null) return 'No reminders';
  // Named per channel rather than merged, because MMS asks the two questions
  // separately and a teacher who set one and not the other needs to see which.
  return [
    `Email: ${leadLabel(email).toLowerCase()}`,
    `SMS: ${leadLabel(sms).toLowerCase()}`,
  ].join(' · ');
};

// Everything the row says about a category that is not its name, its colour or
// its billability.
//
// Make-up credits are on the row because MMS puts them there (#351): its
// category list has a `Make-Up Credit` column, a recycle glyph when the
// checkbox is on and a dash when off. Here it is a clause, because this list
// is rows rather than a table, and stated in both directions: in a summary
// sentence an omitted clause is indistinguishable from a fact nobody mentioned.
const summaryOf = (c) => [
  remindersOf(c),
  allowsMakeupCredits(c) ? 'Make-up credits allowed' : 'No make-up credits',
].join(' · ');

export default function CategoryList({ categories, onRemove, onEdit, busy = false }) {
  const showingDefaults = categories.some((c) => c.isDefault);
  const [menuFor, setMenuFor] = useState(null);

  return (
    <>
      {showingDefaults ? (
        <div className={s.lead}>
          These are the starting categories. Adding or editing one saves the whole
          set to your studio so you can change them freely from then on.
        </div>
      ) : null}

      {/* Said once, above the list, because it is the answer to the question
          this screen provokes: reminders are off on every category to begin
          with, as MMS ships its stock six, and a teacher who ticked "Send email
          lesson reminders" on a student and heard nothing is looking for this
          sentence. */}
      <div className={s.lead}>
        Lesson reminders are off until you set a lead time here. A student is
        reminded only when their own reminder preference is on <em>and</em> the
        lesson&rsquo;s category has reminders enabled.
      </div>

      {categories.map((c) => {
        const billable = categoryIsBillable(c);
        return (
          <div key={c.id} className={s.categoryBlock}>
            <div className={s.row}>
              <span className={s.swatch} style={{ background: c.color }} />
              <span className={s.rowLabel}>{c.label}</span>
              <span className={billable ? s.pillBillable : s.pillFree}>
                {billable ? 'Billable' : 'Not billable'}
              </span>
              <Menu
                open={menuFor === c.id}
                onClose={() => setMenuFor(null)}
                trigger={(
                  <button
                    className={s.kebab}
                    aria-label={`Actions for ${c.label}`}
                    disabled={busy}
                    onClick={() => setMenuFor(menuFor === c.id ? null : c.id)}
                  >
                    ⋮
                  </button>
                )}
              >
                <MenuItem icon="pencil" onClick={() => { setMenuFor(null); onEdit(c); }}>
                  Edit Category
                </MenuItem>
                <MenuItem icon="trash" onClick={() => { setMenuFor(null); onRemove(c.id); }}>
                  Delete Category
                </MenuItem>
              </Menu>
            </div>
            <div className={s.rowSummary}>{summaryOf(c)}</div>
          </div>
        );
      })}
    </>
  );
}
